<script setup lang="ts">
import type { MembershipChangeRequest, MembershipChangeRequestListItem } from './membershipChangeRequestTypes';
import { computed, inject, onMounted, ref } from 'vue';
import EventBus from '../../js/service/EventBus.js';
import KgrEmptyData from '../../components/common/EmptyData.vue';
import KgrIcon from '@legacy/components/common/icon/Icon.vue';
import KgrLoading from '../../components/common/Loading.vue';
import KgrMembershipChangeRequestEditor from './MembershipChangeRequestEditor.vue';
import KgrPaginator from '../../components/common/Paginator.vue';
import MembershipChangeRequestRepository from '../../js/repository/MembershipChangeRequestRepository.js';
import type { Notify } from '@/composables/notifications';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    clubId: number;
}>();

const STATUS_OPEN = 'open';
const STATUS_REJECTED = 'rejected';
const STATUS_ACCEPTED = 'accepted';

const CHANGE_TYPE_SWITCH = 'switch';
const CHANGE_TYPE_JOIN = 'join';
const CHANGE_TYPE_DEPARTURE = 'departure';
const CHANGE_TYPE_TERMINATION = 'termination';

const DEFAULT_PAGE_SIZE = 25;

const SORT_MEMBER = 'member';
const SORT_REQUESTED_AT = 'requestedAt';
const SORT_PROCESSED_AT = 'processedAt';

type SortDirection = 'asc' | 'desc';
type Pagination = { limit: number; offset: number };
type Department = { id: number; name: string };

const notify = inject<Notify>('notify')!;
const status = ref<string>(STATUS_OPEN);
const loading = ref(true);
const processingIds = ref<number[]>([]);
const requests = ref<MembershipChangeRequestListItem[]>([]);
const openCount = ref(0);
const totalCount = ref(0);
const expandedIds = ref<Set<number>>(new Set());
const sort = ref<string>(SORT_REQUESTED_AT);
const direction = ref<SortDirection>('desc');
const pagination = ref<Pagination>({ limit: DEFAULT_PAGE_SIZE, offset: 0 });
const departmentOptions = ref<Department[]>([]);
const selectedDepartmentId = ref<string>('');
const selectedChangeType = ref<string>('');

const isOpen = computed(() => status.value === STATUS_OPEN);
const isProcessedTab = computed(() => status.value !== STATUS_OPEN);
const columnCount = computed(() => (isProcessedTab.value ? 6 : 4));
const hasActiveFilters = computed(() => selectedDepartmentId.value !== '' || selectedChangeType.value !== '');

onMounted(() => {
    loadDepartments();
    selectTab(STATUS_OPEN);
});

function loadDepartments() {
    MembershipChangeRequestRepository.getDepartments(props.clubId)
        .then((result: Department[]) => (departmentOptions.value = result))
        .catch(() => notify.error('error.loadingData'));
}

function onFilterChange() {
    pagination.value = { limit: DEFAULT_PAGE_SIZE, offset: 0 };
    reload();
}

function selectTab(newStatus: string) {
    status.value = newStatus;
    pagination.value = { limit: DEFAULT_PAGE_SIZE, offset: 0 };

    sort.value = newStatus === STATUS_OPEN ? SORT_REQUESTED_AT : SORT_PROCESSED_AT;
    direction.value = 'desc';

    reload();
}

function reload() {
    loading.value = true;
    expandedIds.value = new Set();

    MembershipChangeRequestRepository.getCountAndRequestsByClub(
        props.clubId,
        status.value,
        sort.value,
        direction.value,
        pagination.value.limit,
        pagination.value.offset,
        selectedDepartmentId.value || null,
        selectedChangeType.value || null,
    )
        .then(([count, result]: [number, MembershipChangeRequestListItem[]]) => {
            totalCount.value = count;
            requests.value = result;
            if (status.value === STATUS_OPEN && !hasActiveFilters.value) {
                updateOpenCount(count);
            }
        })
        .catch(() => notify.error('error.loadingData'))
        .finally(() => (loading.value = false));
}

function isSortable(column: string): boolean {
    return column !== SORT_PROCESSED_AT || !isOpen.value;
}

function toggleSort(column: string) {
    if (!isSortable(column)) {
        return;
    }

    if (sort.value === column) {
        direction.value = direction.value === 'asc' ? 'desc' : 'asc';
    } else {
        sort.value = column;
        direction.value = 'asc';
    }

    pagination.value = { ...pagination.value, offset: 0 };
    reload();
}

function sortIndicator(column: string): string {
    if (sort.value !== column) {
        return '↕';
    }

    return direction.value === 'asc' ? '▲' : '▼';
}

function ariaSort(column: string): 'ascending' | 'descending' | 'none' {
    if (sort.value !== column) {
        return 'none';
    }

    return direction.value === 'asc' ? 'ascending' : 'descending';
}

function isActiveSort(column: string): boolean {
    return sort.value === column;
}

function onPaginate(settings: Pagination) {
    pagination.value = settings;
    reload();
}

function updateOpenCount(count: number) {
    openCount.value = count;
    EventBus.$emit('updatedCounterBadge', { name: 'membershipChangeRequestBadge', value: count });
}

function requestTypeLabelKey(request: MembershipChangeRequest): string {
    if (request.type === 'contract_change') {
        switch (request.action) {
            case 'switch_to_active':
                return 'Wechsel zu aktiver Mitgliedschaft';
            case 'switch_to_passive':
                return 'Wechsel zu passiver Mitgliedschaft';
            default:
                return 'Kündigung der Mitgliedschaft';
        }
    }

    if (request.type === 'department_join') {
        return 'Beitritt zu Abteilung';
    }

    return request.type === 'team_termination' ? 'Austritt aus Team' : 'Austritt aus Abteilung';
}

function onProcess(request: MembershipChangeRequest, date: string) {
    processingIds.value = [...processingIds.value, request.id];

    MembershipChangeRequestRepository.process(request.id, date)
        .then(() => {
            notify.success('saveChanges.success');
            reload();
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => (processingIds.value = processingIds.value.filter(id => id !== request.id)));
}

function onProcessDeletion(request: MembershipChangeRequest) {
    processingIds.value = [...processingIds.value, request.id];

    MembershipChangeRequestRepository.processDeletion(request.id)
        .then(() => {
            notify.success('saveChanges.success');
            reload();
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => (processingIds.value = processingIds.value.filter(id => id !== request.id)));
}

function onReject(request: MembershipChangeRequest) {
    processingIds.value = [...processingIds.value, request.id];

    MembershipChangeRequestRepository.reject(request.id)
        .then(() => {
            notify.success('saveChanges.success');
            reload();
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => (processingIds.value = processingIds.value.filter(id => id !== request.id)));
}

function isExpanded(requestId: number): boolean {
    return expandedIds.value.has(requestId);
}

function toggleExpand(requestId: number) {
    const next = new Set(expandedIds.value);
    if (next.has(requestId)) {
        next.delete(requestId);
    } else {
        next.add(requestId);
    }
    expandedIds.value = next;
}
</script>

<template>
    <div>
        <div class="kgr-tabs">
            <div class="kgr-tabs__headers kgr-row kgr-row--bigMargin">
                <a
                    class="kgr-clickable kgr-tabs__tab"
                    :class="{ 'is-active': isOpen }"
                    @click="selectTab(STATUS_OPEN)"
                >
                    {{ $t('Offene Anträge') }}
                    <span
                        class="kgr-tabs__badge kgr-badge"
                        :class="{ 'kgr-badge--highlight': openCount > 0, 'kgr-badge--info': openCount === 0 }"
                    >
                        {{ openCount }}
                    </span>
                </a>
                <a
                    class="kgr-clickable kgr-tabs__tab"
                    :class="{ 'is-active': status === STATUS_REJECTED }"
                    @click="selectTab(STATUS_REJECTED)"
                >
                    {{ $t('Abgelehnte Anträge') }}
                </a>
                <a
                    class="kgr-clickable kgr-tabs__tab"
                    :class="{ 'is-active': status === STATUS_ACCEPTED }"
                    @click="selectTab(STATUS_ACCEPTED)"
                >
                    {{ $t('Übernommene Anträge') }}
                </a>
            </div>
        </div>

        <div class="kgr-postfix kgr-postfix--gutters kgr-row kgr-row--bigMargin">
            <div class="kgr-postfix__fixed">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('Abteilung') }}</span>
                    <select
                        v-model="selectedDepartmentId"
                        class="kgr-select"
                        @change="onFilterChange"
                    >
                        <option value="">
                            {{ $t('Alle') }}
                        </option>
                        <option
                            v-for="department in departmentOptions"
                            :key="department.id"
                            :value="String(department.id)"
                        >
                            {{ department.name }}
                        </option>
                    </select>
                </label>
            </div>
            <div class="kgr-postfix__fixed">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('Beantragte Änderungen') }}</span>
                    <select
                        v-model="selectedChangeType"
                        class="kgr-select"
                        @change="onFilterChange"
                    >
                        <option value="">
                            {{ $t('Alle') }}
                        </option>
                        <option :value="CHANGE_TYPE_SWITCH">
                            {{ $t('Wechsel') }}
                        </option>
                        <option :value="CHANGE_TYPE_JOIN">
                            {{ $t('Beitritte') }}
                        </option>
                        <option :value="CHANGE_TYPE_DEPARTURE">
                            {{ $t('Austritte') }}
                        </option>
                        <option :value="CHANGE_TYPE_TERMINATION">
                            {{ $t('Kündigung') }}
                        </option>
                    </select>
                </label>
            </div>
        </div>

        <kgr-loading v-if="loading" />
        <div v-else>
            <kgr-empty-data
                v-if="requests.length === 0"
                :value="$t('Keine Anträge vorhanden.')"
            />
            <table
                v-else
                class="kgr-table kgr-table--striped kgr-table--block kgr-row"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th
                            class="kgr-table__cell kgr-table__cell--headline kgr-clickable sortableHeader"
                            :class="{ 'is-activeSort': isActiveSort(SORT_MEMBER) }"
                            style="width: 30%"
                            :aria-sort="ariaSort(SORT_MEMBER)"
                            @click="toggleSort(SORT_MEMBER)"
                        >
                            {{ $t('Mitglied') }}
                            <span class="sortIndicator">{{ sortIndicator(SORT_MEMBER) }}</span>
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Beantragte Änderungen') }}
                        </th>
                        <th
                            class="kgr-table__cell kgr-table__cell--headline kgr-clickable sortableHeader"
                            :class="{ 'is-activeSort': isActiveSort(SORT_REQUESTED_AT) }"
                            :aria-sort="ariaSort(SORT_REQUESTED_AT)"
                            @click="toggleSort(SORT_REQUESTED_AT)"
                        >
                            {{ $t('Beantragt am') }}
                            <span class="sortIndicator">{{ sortIndicator(SORT_REQUESTED_AT) }}</span>
                        </th>
                        <th
                            v-if="isProcessedTab"
                            class="kgr-table__cell kgr-table__cell--headline kgr-clickable sortableHeader"
                            :class="{ 'is-activeSort': isActiveSort(SORT_PROCESSED_AT) }"
                            :aria-sort="ariaSort(SORT_PROCESSED_AT)"
                            @click="toggleSort(SORT_PROCESSED_AT)"
                        >
                            {{ $t('Bearbeitet am') }}
                            <span class="sortIndicator">{{ sortIndicator(SORT_PROCESSED_AT) }}</span>
                        </th>
                        <th
                            v-if="isProcessedTab"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Bearbeitet von') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline expandCell"></th>
                    </tr>
                </thead>
                <tbody>
                    <template
                        v-for="request in requests"
                        :key="request.id"
                    >
                        <tr
                            class="kgr-table__row kgr-clickable"
                            :class="{ 'is-expanded': isExpanded(request.id) }"
                            @click="toggleExpand(request.id)"
                        >
                            <td class="kgr-table__cell">
                                <strong class="kgr-text kgr-text--bold">{{ request.memberName }}</strong>
                            </td>
                            <td class="kgr-table__cell">
                                {{ $t(requestTypeLabelKey(request)) }}<template v-if="request.targetName">
                                    ({{ request.targetName }})
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                {{ request.requestedAt }}
                            </td>
                            <td
                                v-if="isProcessedTab"
                                class="kgr-table__cell"
                            >
                                {{ request.processedAt ?? request.rejectedAt ?? '–' }}
                            </td>
                            <td
                                v-if="isProcessedTab"
                                class="kgr-table__cell"
                            >
                                {{ request.processedBy ?? '–' }}
                            </td>
                            <td class="kgr-table__cell expandCell">
                                <kgr-icon
                                    icon-class="arrow-right"
                                    class="expandIcon"
                                    :class="{ 'is-expanded': isExpanded(request.id) }"
                                />
                            </td>
                        </tr>
                        <tr
                            v-if="isExpanded(request.id)"
                            class="kgr-table__row"
                        >
                            <td
                                class="kgr-table__cell"
                                :colspan="columnCount"
                            >
                                <kgr-membership-change-request-editor
                                    :request="request"
                                    :member-id="request.memberId"
                                    :processing="processingIds.includes(request.id)"
                                    @process="onProcess"
                                    @process-deletion="onProcessDeletion"
                                    @reject="onReject"
                                />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>

            <kgr-paginator
                v-if="totalCount > 0"
                :value="pagination"
                :length="totalCount"
                @input="onPaginate"
            />
        </div>
    </div>
</template>

<style scoped lang="scss" rel="stylesheet/scss">
.sortableHeader {
    white-space: nowrap;
}

.sortableHeader.is-activeSort {
    font-weight: bold;
}

.sortIndicator {
    vertical-align: middle;
    margin-left: 4px;
    opacity: 0.6;
    font-size: 1.4em;

    .sortableHeader.is-activeSort & {
        font-size: 0.8em;
    }
}

.expandCell {
    width: 1px;
    text-align: right;
    white-space: nowrap;
}

.expandIcon {
    transition: transform 0.2s ease;

    &.is-expanded {
        transform: rotate(90deg);
    }
}

</style>
